import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

const Home = ({ language }) => {
  const [activeStat, setActiveStat] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isRevealing, setIsRevealing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsRevealing(false);
    }, 1800); // 1.8 seconds reveal duration
    return () => clearTimeout(timer);
  }, []);

  const handlePrev = () => {
    if (!activeStat || !activeStat.images || !selectedImage) return;
    const len = activeStat.images.length;
    const prevIndex = ((selectedImage.index - 1) + len) % len;
    setSelectedImage({ ...activeStat.images[prevIndex], index: prevIndex });
  };

  const handleNext = () => {
    if (!activeStat || !activeStat.images || !selectedImage) return;
    const len = activeStat.images.length;
    const nextIndex = (selectedImage.index + 1) % len;
    setSelectedImage({ ...activeStat.images[nextIndex], index: nextIndex });
  };

  const content = {
    en: {
      welcomeTitle: "Governor's Welcome Message",
      welcomeMessage:
        "Welcome to the website that showcases the progress of the Kano State Government under the leadership of Governor Abba Kabir Yusuf. We are committed to transforming Kano State through strategic investments in infrastructure, education, healthcare, and youth empowerment.",
      overviewTitle: "About This Site",
      overviewText:
        "This platform highlights the achievements, policies, and development initiatives of the Kano State Government. Explore our pages to learn more about our shared vision for a greater Kano.",
      exploreTitle: "Explore Our Pages",
      pages: [
        { title: "About AKY", description: "Learn about Governor Abba Kabir Yusuf", path: "/about" },
        { title: "Projects", description: "View ongoing and completed projects", path: "/projects" },
        { title: "Awards", description: "Recognition and achievements", path: "/awards" },
        { title: "How to Vote", description: "Learn how to participate in elections", path: "/how-to-vote" },
        { title: "Polling Units", description: "Find Kano polling units and get directions", path: "/polling-units" },
        { title: "Snapshots", description: "Gallery of key events and pictures", path: "/snapshots" },
        { title: "FAQ", description: "Frequently asked questions", path: "/faq" },
        { title: "Contact Us", description: "Get in touch with the Governor", path: "/contacts" },
      ],
      stats: [
        {
          label: "Teachers Recruited",
          value: "10,000+",
          desc: "Snapshots of some of the teachers recruited under the administration of Governor Abba Kabir Yusuf.",
          images: [
            { src: "/Teacherone.jpg", alt: "Teacher 1" },
            { src: "/Teachertwo.jpg", alt: "Teacher 2" },
            { src: "/Teacherthree.jpg", alt: "Teacher 3" },
            { src: "/Teacherfour.jpg", alt: "Teacher 4" },
            { src: "/Teacherfive(3).jpg", alt: "Teacher 5" },
            { src: "/Teacherfive(4).jpg", alt: "Teacher 5" },
            { src: "/Teacherfive(5).jpg", alt: "Teacher 6" },
            { src: "/Teacherfive(6).jpg", alt: "Teacher 7" },
            { src: "/Teacherfive(7).jpg", alt: "Teacher 8" },
            { src: "/Teacherfive(8).jpg", alt: "Teacher 9" },
            { src: "/Teacherfive(9).jpg", alt: "Teacher 10" },
            { src: "/Teacherfive(10).jpg", alt: "Teacher 11" },
            { src: "/Teacherfive(11).jpg", alt: "Teacher 12" },
            { src: "/Teacherfive(12).jpg", alt: "Teacher 13" },
            { src: "/Teacherfive(13).jpg", alt: "Teacher 14" },
            { src: "/Teacherfive(14).jpg", alt: "Teacher 15" },
            { src: "/Teacherfive(15).jpg", alt: "Teacher 16" },
            { src: "/Teacherfive(16).jpg", alt: "Teacher 17" },
            { src: "/Teacherfive(17).jpg", alt: "Teacher 18" },
            { src: "/Teacherfive(18).jpg", alt: "Teacher 19" },
            { src: "/Teacherfive(19).jpg", alt: "Teacher 20" },
          ],
        },
        {
          label: "Youth Empowered",
          value: "10,500+",
          desc: "Youths empowered through entrepreneurship and employment programs.",
          images: [
            { src: "/Youth(1).jpg", alt: "Youth 1" },
            { src: "/Youth(2).jpg", alt: "Youth 2" },
            { src: "/Youth(3).jpg", alt: "Youth 3" },
            { src: "/Youth(4).jpg", alt: "Youth 4" },
            { src: "/Youth(5).jpg", alt: "Youth 5" },
            { src: "/Youth(6).jpg", alt: "Youth 6" },
            { src: "/Youth(7).jpg", alt: "Youth 7" },
            { src: "/Youth(8).jpg", alt: "Youth 8" },
            { src: "/Youth(9).jpg", alt: "Youth 9" },
            { src: "/Youth(10).jpg", alt: "Youth 10" },
            { src: "/Youth(11).jpg", alt: "Youth 11" },
            { src: "/Youth(12).jpg", alt: "Youth 12" },
            { src: "/Youth(13).jpg", alt: "Youth 13" },
            { src: "/Youth(14).jpg", alt: "Youth 14" },
            { src: "/Youth(15).jpg", alt: "Youth 15" },
            { src: "/Youthone(1).jpg", alt: "Youth 16" },
            { src: "/Youthone(10).jpg", alt: "Youth 17" },
            { src: "/Youthone(5).jpg", alt: "Youth 18" },
            { src: "/Youthone(9).jpg", alt: "Youth 19" },
            { src: "/Youthone(6).jpg", alt: "Youth 20" },
          ],
        },
        {
          label: "Schools Renovated",
          value: "100+",
          desc: "Snapshots of some of the schools renovated across Kano State.",
          images: [
            { src: "/Schoolone.jpg", alt: "School 1" },
            { src: "/Schoolone.jpg", alt: "School 2 (fallback)" },
            { src: "/Schoolthree.jpeg", alt: "School 3" },
            { src: "/Schoolfour.jpeg", alt: "School 4" },
            { src: "/Schoolfive.jpeg", alt: "School 5" },
            { src: "/Schoolsix.jpeg", alt: "School 6" },
            { src: "/Schoolseven.jpeg", alt: "School 7" },
            { src: "/Schooleight.jpeg", alt: "School 8" },
            { src: "/Schoolnine.jpeg", alt: "School 9" },
            { src: "/Schoolten.jpeg", alt: "School 10" },
          ],
        },
        {
          label: "Infrastructure Projects",
          value: "100+",
          desc: "Some of the infrastructure projects improving roads and communities.",
          images: [
            { src: "/Infrastrutureone.jpeg", alt: "Infrastructure 1" },
            { src: "/Infrastruturetwo.jpg", alt: "Infrastructure 2" },
            { src: "/Infrastruturethree.jpeg", alt: "Infrastructure 3" },
            { src: "/Infrastruturefour.jpeg", alt: "Infrastructure 4" },
            { src: "/Infrastruturefive.jpeg", alt: "Infrastructure 5" },
            { src: "/Infrastruturesix.jpeg", alt: "Infrastructure 6" },
            { src: "/Infrastrutureseven.jpeg", alt: "Infrastructure 7" },
            { src: "/Infrastrutureeight.jpeg", alt: "Infrastructure 8" },
            { src: "/Infrastruturenine.jpeg", alt: "Infrastructure 9" },
            { src: "/Infrastructureten.jpeg", alt: "Infrastructure 10" },
            { src: "/Infrastructureeleven.jpg", alt: "Infrastructure 11" },
            { src: "/Infrastructuretwelve.jpg", alt: "Infrastructure 12" },
            { src: "/Infrastructurefourteen.jpg", alt: "Infrastructure 14" },
            { src: "/Infrastructurefifteen.jpeg", alt: "Infrastructure 15" },
            { src: "/Infrastructuresixteen.jpeg", alt: "Infrastructure 16" },
            { src: "/Infrastructureseventeen.jpeg", alt: "Infrastructure 17" },
            { src: "/Infrastructureeighteen.jpeg", alt: "Infrastructure 18" },
            { src: "/Infrastructurenineteen.jpeg", alt: "Infrastructure 19" },
            { src: "/Infrastructuretwenty.jpeg", alt: "Infrastructure 20" },
            { src: "/ungoggo(2).jpg", alt: "Infrastructure 21" },
            { src: "/ungoggo(3).jpg", alt: "Infrastructure 22" },
            { src: "/ungoggo(4).jpg", alt: "Infrastructure 23" },
            { src: "/ungoggo(5).jpg", alt: "Infrastructure 24" },
            { src: "/ungoggo(6).jpg", alt: "Infrastructure 25" },
          ],
        },
        {
          label: "Healthcare Centers Improved",
          value: "500+",
          desc: "Snapshots of some of the health centers renovated and equipped for better service.",
          images: [
            { src: "/Healthone.jpeg", alt: "Health 1" },
            { src: "/Healthtwo.jpeg", alt: "Health 2" },
            { src: "/Healththree.jpeg", alt: "Health 3" },
            { src: "/Healthfour.jpeg", alt: "Health 4" },
            { src: "/NEW(29).jpg", alt: "Health 5" },
          ],
        },
      ],
    },
    ha: {
      welcomeTitle: "Sakonnin Gwamna",
      welcomeMessage:
        "Barka da zuwa shafin yanar gizo wanda ke nuna ci gaban Gwamnatin Jihar Kano ƙarƙashin jagorancin Gwamna Abba Kabir Yusuf. Munhimminmu shine canza Jihar Kano ta hanyar zuba jari mai kyau a cikin ababen more rayuwa, ilimi, lafiya, da karfafa matasa.",
      overviewTitle: "Game da wannan shafin",
      overviewText:
        "Wannan dandali yana haskaka nasarori, manufofi, da ayyukan ci gaban Gwamnatin Jihar Kano. Ziyarci shafukanmu don koyo game da hangen nesa ɗaya da muke da shi na Kano mai girma.",
      exploreTitle: "Bincika Shafukanmu",
      pages: [
        { title: "Game da AKY", description: "Koyi game da Gwamna Abba Kabir Yusuf", path: "/about" },
        { title: "Ayyuka", description: "Duba ayyukan da ake ciki da kuma waɗanda aka kammala", path: "/projects" },
        { title: "Lambobin yabo", description: "Girmamawa da nasarori", path: "/awards" },
        { title: "Hoto", description: "Galeriya ta muhimman abubuwan da suka faru da hotuna", path: "/snapshots" },
        { title: "Wuraren Zabe", description: "Nemo wuraren zaben Kano da samun umarnin tafiya", path: "/polling-units" },
        { title: "Tambayoyi", description: "Tambayoyi akai-akai", path: "/faq" },
        { title: "Tuntube Mu", description: "Tuntubi Gwamna", path: "/contacts" },
      ],
      stats: [
        {
          label: "Malaman da aka dauka",
          value: "10,000+",
          desc: "Hotunan wasu daga cikin malamai da aka dauka a ƙarƙashin mulkin Gwamna Abba Kabir Yusuf.",
          images: [
            { src: "/Teacherone.jpg", alt: "Teacher 1" },
            { src: "/Teachertwo.jpg", alt: "Teacher 2" },
            { src: "/Teacherthree.jpg", alt: "Teacher 3" },
            { src: "/Teacherfour.jpg", alt: "Teacher 4" },
            { src: "/Teacherfive(3).jpg", alt: "Teacher 5" },
            { src: "/Teacherfive(4).jpg", alt: "Teacher 5" },
            { src: "/Teacherfive(5).jpg", alt: "Teacher 6" },
            { src: "/Teacherfive(6).jpg", alt: "Teacher 7" },
            { src: "/Teacherfive(7).jpg", alt: "Teacher 8" },
            { src: "/Teacherfive(8).jpg", alt: "Teacher 9" },
            { src: "/Teacherfive(9).jpg", alt: "Teacher 10" },
            { src: "/Teacherfive(10).jpg", alt: "Teacher 11" },
            { src: "/Teacherfive(11).jpg", alt: "Teacher 12" },
            { src: "/Teacherfive(12).jpg", alt: "Teacher 13" },
            { src: "/Teacherfive(13).jpg", alt: "Teacher 14" },
            { src: "/Teacherfive(14).jpg", alt: "Teacher 15" },
            { src: "/Teacherfive(15).jpg", alt: "Teacher 16" },
            { src: "/Teacherfive(16).jpg", alt: "Teacher 17" },
            { src: "/Teacherfive(17).jpg", alt: "Teacher 18" },
            { src: "/Teacherfive(18).jpg", alt: "Teacher 19" },
            { src: "/Teacherfive(19).jpg", alt: "Teacher 20" },
          ],
        },
        {
          label: "Matasan da aka tallafa",
          value: "10,500+",
          desc: "Matasan da aka tallafa ta hanyar shirye-shiryen kasuwanci da aikin yi.",
          images: [
            { src: "/Youth(1).jpg", alt: "Youth 1" },
            { src: "/Youth(2).jpg", alt: "Youth 2" },
            { src: "/Youth(3).jpg", alt: "Youth 3" },
            { src: "/Youth(4).jpg", alt: "Youth 4" },
            { src: "/Youth(5).jpg", alt: "Youth 5" },
            { src: "/Youth(6).jpg", alt: "Youth 6" },
            { src: "/Youth(7).jpg", alt: "Youth 7" },
            { src: "/Youth(8).jpg", alt: "Youth 8" },
            { src: "/Youth(9).jpg", alt: "Youth 9" },
            { src: "/Youth(10).jpg", alt: "Youth 10" },
            { src: "/Youth(11).jpg", alt: "Youth 11" },
            { src: "/Youth(12).jpg", alt: "Youth 12" },
            { src: "/Youth(13).jpg", alt: "Youth 13" },
            { src: "/Youth(14).jpg", alt: "Youth 14" },
            { src: "/Youth(15).jpg", alt: "Youth 15" },
            { src: "/Youthone(1).jpg", alt: "Youth 16" },
            { src: "/Youthone(10).jpg", alt: "Youth 17" },
            { src: "/Youthone(5).jpg", alt: "Youth 18" },
            { src: "/Youthone(9).jpg", alt: "Youth 19" },
            { src: "/Youthone(6).jpg", alt: "Youth 20" },
          ],
        },
        {
          label: "Makarantun da aka gyara",
          value: "100+",
          desc: "Hotunan wasu daga cikin makarantu da aka gyara a fadin Jihar Kano.",
          images: [
            { src: "/Schoolone.jpg", alt: "School 1" },
            { src: "/Schoolone.jpg", alt: "School 2 (fallback)" },
            { src: "/Schoolthree.jpeg", alt: "School 3" },
            { src: "/Schoolfour.jpeg", alt: "School 4" },
            { src: "/Schoolfive.jpeg", alt: "School 5" },
            { src: "/Schoolsix.jpeg", alt: "School 6" },
            { src: "/Schoolseven.jpeg", alt: "School 7" },
            { src: "/Schooleight.jpeg", alt: "School 8" },
            { src: "/Schoolnine.jpeg", alt: "School 9" },
            { src: "/Schoolten.jpeg", alt: "School 10" },
          ],
        },
        {
          label: "Ayyukan More Rayuwa",
          value: "100+",
          desc: "Wasu daga cikin ayyukan more rayuwa da ke inganta hanyoyi da al'ummomi.",
          images: [
            { src: "/Infrastrutureone.jpeg", alt: "Infrastructure 1" },
            { src: "/Infrastruturetwo.jpg", alt: "Infrastructure 2" },
            { src: "/Infrastruturethree.jpeg", alt: "Infrastructure 3" },
            { src: "/Infrastruturefour.jpeg", alt: "Infrastructure 4" },
            { src: "/Infrastruturefive.jpeg", alt: "Infrastructure 5" },
            { src: "/Infrastruturesix.jpeg", alt: "Infrastructure 6" },
            { src: "/Infrastrutureseven.jpeg", alt: "Infrastructure 7" },
            { src: "/Infrastrutureeight.jpeg", alt: "Infrastructure 8" },
            { src: "/Infrastruturenine.jpeg", alt: "Infrastructure 9" },
            { src: "/Infrastructureten.jpeg", alt: "Infrastructure 10" },
            { src: "/Infrastructureeleven.jpg", alt: "Infrastructure 11" },
            { src: "/Infrastructuretwelve.jpg", alt: "Infrastructure 12" },
            { src: "/Infrastructurefourteen.jpg", alt: "Infrastructure 14" },
            { src: "/Infrastructurefifteen.jpeg", alt: "Infrastructure 15" },
            { src: "/Infrastructuresixteen.jpeg", alt: "Infrastructure 16" },
            { src: "/Infrastructureseventeen.jpeg", alt: "Infrastructure 17" },
            { src: "/Infrastructureeighteen.jpeg", alt: "Infrastructure 18" },
            { src: "/Infrastructurenineteen.jpeg", alt: "Infrastructure 19" },
            { src: "/Infrastructuretwenty.jpeg", alt: "Infrastructure 20" },
            { src: "/ungoggo(2).jpg", alt: "Infrastructure 21" },
            { src: "/ungoggo(3).jpg", alt: "Infrastructure 22" },
            { src: "/ungoggo(4).jpg", alt: "Infrastructure 23" },
            { src: "/ungoggo(5).jpg", alt: "Infrastructure 24" },
            { src: "/ungoggo(6).jpg", alt: "Infrastructure 25" },
          ],
        },
        {
          label: "Cibiyoyin Lafiya da aka gyara",
          value: "500+",
          desc: "Hotunan wasu daga cikin cibiyoyin lafiya da aka gyara da kuma inganta don samar da mafi kyawun sabis.",
          images: [
            { src: "/Healthone.jpeg", alt: "Health 1" },
            { src: "/Healthtwo.jpeg", alt: "Health 2" },
            { src: "/Healththree.jpeg", alt: "Health 3" },
            { src: "/Healthfour.jpeg", alt: "Health 4" },
            { src: "/NEW(29).jpg", alt: "Health 5" },
          ],
        },
      ],
    },
  };

  const current = content[language];

  return (
    <div className={`home ${isRevealing ? 'is-revealing' : 'is-flooded'}`}>
      <div className="hero-section">
        <img src="/HOMEPIC.jpg" alt="Home" className="hero-image" />
        <div className="hero-overlay">
          <h1 className="hero-title">Continuity Abba 4 + 4</h1>
        </div>
      </div>

      <div className="home-container">
        {/* Info Cards */}
        <section className="cards-row">
          <div className="info-card">
            <h3>{current.welcomeTitle}</h3>
            <p>{current.welcomeMessage}</p>
          </div>

          <div className="info-card">
            <h3>{current.overviewTitle}</h3>
            <p>{current.overviewText}</p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <div className="stats-grid">
            {current.stats.map((stat, idx) => (
              <button key={idx} className="stat-card" onClick={() => setActiveStat(stat)}>
                <h4>{stat.label}</h4>
                <p>{stat.value}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Explore Section */}
        <section className="pages-section">
          <h2>{current.exploreTitle}</h2>
          <div className="pages-grid">
            {current.pages.map((page, index) => (
              <Link key={index} to={page.path} className="page-card">
                <h3>{page.title}</h3>
                <p>{page.description}</p>
                <span className="arrow">→</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* Modal for Stat Images */}
      {activeStat && (
        <div className="stat-modal-overlay" onClick={() => setActiveStat(null)}>
          <div className="stat-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-x"
              onClick={() => { setActiveStat(null); setSelectedImage(null); }}
              aria-label="Close modal"
            >
              ✕
            </button>
            <h2>{activeStat.label}</h2>
            <p>{activeStat.desc}</p>
            <div
              className={`stat-images ${["Schools Renovated", "Infrastructure Projects", "Healthcare Centers Improved"].includes(activeStat.label) ? 'reduced' : ''}`}
            >
              {activeStat.images && activeStat.images.length > 0 ? (
                activeStat.images.map((img, i) => (
                  <img
                    key={i}
                    src={img.src}
                    alt={img.alt || `${activeStat.label} ${i + 1}`}
                    onClick={() => setSelectedImage({ ...img, index: i })}
                  />
                ))
              ) : (
                <p>No images available for this item.</p>
              )}
            </div>

            {selectedImage && (
              <div className="single-image-view full-card" onClick={(e) => e.stopPropagation()}>
                <button
                  className="image-close-btn"
                  onClick={() => setSelectedImage(null)}
                  aria-label="Close image"
                >
                  ✕
                </button>

                <button className="nav-btn left" onClick={handlePrev} aria-label="Previous image">
                  ‹
                </button>

                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt || activeStat.label}
                  className="single-img"
                />

                <button className="nav-btn right" onClick={handleNext} aria-label="Next image">
                  ›
                </button>

                <div className="image-counter">
                  {selectedImage.index + 1} / {activeStat.images.length}
                </div>
              </div>
            )}
            <button className="close-btn" onClick={() => setActiveStat(null)}>
              ✕ Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
